import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { SearchIcon, SmallLeftArrow, CloseIcon } from 'assets/icons/icons'
import { PiDnaFill } from 'react-icons/pi'
import { useSearch } from './SearchContext'
import { SearchItem } from './types'
import * as styles from './Helix.css'
import { Box } from 'components/layout/box'
import { Text } from 'components/typography/text'
import { BlockIcon } from 'components/icons/block-icon'
import { useVirtualizer } from '@tanstack/react-virtual'
import { useArenaContext } from 'context/ArenaContext'
import { useTabsContext } from 'components/Tabs/TabsProvider'
import { useNavigatorSummaryInfo } from 'api/navigator/fetchNavigatorSummaryInfo'
import { IconButton } from 'components/inputs/icon-button'
import { ResultItem } from './components/ResultItem'
import { StockContainer } from './components/StockContainer'
import { useSearchResults } from './hooks/useSearchResults'
import { useStockOptions } from './hooks/useStockOptions'
import { useKeyboardNavigation } from './hooks/useKeyboardNavigation'
import { useStockKeyboardNavigation } from './hooks/useStockKeyboardNavigation'
import { useAnalyticsSuggestions } from './hooks/useAnalyticsSuggestions'

// Custom hook for debounced value
function useDebounce<T>(value: T, delay: number): T {
	const [debouncedValue, setDebouncedValue] = useState<T>(value)

	useEffect(() => {
		const handler = setTimeout(() => {
			setDebouncedValue(value)
		}, delay)

		return () => {
			clearTimeout(handler)
		}
	}, [value, delay])

	return debouncedValue
}

const Helix: React.FC = () => {
	const { isOpen, closeSearch } = useSearch()
	const [inputValue, setInputValue] = useState('')
	const [stockSearchValue, setStockSearchValue] = useState('')
	const [selectedIndex, setSelectedIndex] = useState(0)
	const [selectedOptionIndex, setSelectedOptionIndex] = useState(0)
	const [selectedStock, setSelectedStock] = useState<SearchItem | null>(null)
	const [stockContainerVisible, setStockContainerVisible] = useState(false)
	const [isStockSearching, setIsStockSearching] = useState(false)
	const inputRef = useRef<HTMLInputElement>(null)
	const stockInputRef = useRef<HTMLInputElement>(null)
	const resultsRef = useRef<HTMLDivElement>(null)
	const navigate = useNavigate()
	const { onActiveTabChange } = useTabsContext()
	const { setArenaSelectedBotName } = useArenaContext()

	// Detect if user is on macOS
	const isMac = useMemo(() => {
		return navigator.platform.toUpperCase().indexOf('MAC') >= 0
	}, [])

	// Get the appropriate modifier key symbol
	const modifierKey = useMemo(() => {
		return isMac ? '⌘' : 'Ctrl'
	}, [isMac])

	const query = useDebounce(inputValue, 300)
	const stockQuery = useDebounce(stockSearchValue, 100)

	// Use custom hooks for search results and analytics suggestions
	const { searchResults, isSearching, isLoadingData } = useSearchResults(query)
	const { suggestions, isLoading: isLoadingSuggestions } = useAnalyticsSuggestions(isOpen)

	// Determine which results to show based on whether user has started typing
	const displayResults = useMemo(() => {
		if (inputValue.trim()) {
			return searchResults || []
		}
		return suggestions || []
	}, [inputValue, searchResults, suggestions])

	// Extract ticker from selected stock when available
	const ticker = useMemo(() => {
		if (!selectedStock) return null
		const tickerMatch = selectedStock.label.match(/\(([A-Z0-9]+)\)/)
		return tickerMatch ? tickerMatch[1].toUpperCase() : null
	}, [selectedStock])

	// Fetch summary info when a stock is selected and container is visible
	const { data: companyInfoData, isLoading: isCompanyInfoLoading } = useNavigatorSummaryInfo(ticker)

	// Use custom hooks for stock options
	const stockOptions = useStockOptions(ticker)

	// Show loading state when input changes but debounce hasn't completed
	useEffect(() => {
		setIsStockSearching(stockSearchValue !== stockQuery && stockSearchValue.length > 0)
	}, [stockSearchValue, stockQuery])

	// Reset stock container state when closing or changing query
	useEffect(() => {
		if (!isOpen || query !== '') {
			setStockContainerVisible(false)
			setSelectedStock(null)
			setStockSearchValue('')
		}
	}, [isOpen, query])

	// Focus appropriate input when container changes
	useEffect(() => {
		if (isOpen) {
			if (stockContainerVisible && stockInputRef.current) {
				stockInputRef.current.focus()
			} else if (inputRef.current) {
				inputRef.current.focus()
			}
		}
	}, [isOpen, stockContainerVisible])

	// Initialize virtualizer
	const rowVirtualizer = useVirtualizer({
		count: displayResults.length,
		getScrollElement: () => resultsRef.current,
		estimateSize: () => 40,
		overscan: 5
	})

	// Scroll to selected item when it changes
	useEffect(() => {
		if (selectedIndex >= 0 && displayResults.length > 0) {
			rowVirtualizer.scrollToIndex(selectedIndex, { align: 'center' })
		}
	}, [selectedIndex, rowVirtualizer, displayResults.length])

	// Focus input when modal opens
	useEffect(() => {
		if (isOpen && inputRef.current && !stockContainerVisible) {
			inputRef.current.focus()
			setInputValue('')
			setSelectedIndex(0)
		}
	}, [isOpen, stockContainerVisible])

	// Update selected index when filtered results change
	useEffect(() => {
		if (!displayResults || displayResults.length === 0) {
			setSelectedIndex(0)
			return
		}

		if (selectedIndex >= displayResults.length) {
			// Reset to first non-header item
			let firstNonHeaderIndex = 0
			while (
				firstNonHeaderIndex < displayResults.length &&
				(displayResults[firstNonHeaderIndex] as any)?.isGroupHeader
			) {
				firstNonHeaderIndex++
			}

			setSelectedIndex(firstNonHeaderIndex < displayResults.length ? firstNonHeaderIndex : 0)
		} else if ((displayResults[selectedIndex] as any)?.isGroupHeader) {
			// If current selection is a header, move to next non-header
			let nextIndex = selectedIndex + 1
			while (nextIndex < displayResults.length && (displayResults[nextIndex] as any)?.isGroupHeader) {
				nextIndex++
			}

			if (nextIndex < displayResults.length) {
				setSelectedIndex(nextIndex)
			} else {
				// If no items after header, search backwards
				let prevIndex = selectedIndex - 1
				while (prevIndex >= 0 && (displayResults[prevIndex] as any)?.isGroupHeader) {
					prevIndex--
				}

				setSelectedIndex(prevIndex >= 0 ? prevIndex : 0)
			}
		}
	}, [displayResults, selectedIndex])

	// Handle bot selection
	const handleBotClick = useCallback(
		(botName: string) => {
			if (!botName) {
				return
			}

			navigate('/mavbots-arena')
			onActiveTabChange('Roster')
			setArenaSelectedBotName(botName.toLowerCase())
			closeSearch()
		},
		[onActiveTabChange, navigate, closeSearch]
	)

	// Handle suggestion selection
	const handleSuggestionSelect = useCallback((suggestion: SearchItem) => {
		setInputValue(suggestion.label)
	}, [])

	// Handle normal item selection
	const handleSelect = useCallback(
		(item: SearchItem) => {
			// For suggestions, just set the input value
			if (item.type === 'suggestion') {
				handleSuggestionSelect(item)
				return
			}

			// For stock items, open the stock container instead of navigating
			if (item.type === 'stock' || item.type === 'etf') {
				setSelectedStock(item)
				setStockContainerVisible(true)
				setSelectedOptionIndex(0)
				setStockSearchValue('')

				// Focus the stock search input after a short delay to ensure it's rendered
				setTimeout(() => {
					if (stockInputRef.current) {
						stockInputRef.current.focus()
					}
				}, 10)
				return
			}

			// For bot items, use the handleBotClick function
			// if (item.type === 'bot' && item.botData) {
			if (item.type === 'bot') {
				// console.log('botData', item.botData)

				handleBotClick(item.label)
				return
			}

			// For other items, navigate normally
			if (item.to) {
				navigate(item.to)
			} else if (item.action) {
				item.action()
			}
			closeSearch()
		},
		[navigate, closeSearch, handleBotClick, handleSuggestionSelect]
	)

	// Handle stock option selection
	const handleStockOptionSelect = useCallback(
		(path: string) => {
			navigate(path)
			closeSearch()
		},
		[navigate, closeSearch]
	)

	// Return to main search from stock container
	const returnToMainSearch = useCallback(() => {
		setStockContainerVisible(false)
		setSelectedStock(null)
		setStockSearchValue('')
		setTimeout(() => {
			if (inputRef.current) {
				inputRef.current.focus()
			}
		}, 10)
	}, [])

	// Use custom hooks for keyboard navigation
	const handleKeyDown = useKeyboardNavigation({
		displayResults,
		selectedIndex,
		setSelectedIndex,
		onSelect: handleSelect,
		closeSearch
	})

	const handleStockContainerKeyDown = useStockKeyboardNavigation({
		stockOptions,
		selectedOptionIndex,
		setSelectedOptionIndex,
		onSelect: handleStockOptionSelect,
		returnToMainSearch,
		stockSearchValue
	})

	// Handle input change with immediate UI feedback
	const handleInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
		const value = e.target.value
		setInputValue(value)
	}, [])

	// Handle stock search input change
	const handleStockSearchChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
		const value = e.target.value
		setStockSearchValue(value)
		// Reset the selected option index when searching
		setSelectedOptionIndex(0)
	}, [])

	// Close if clicked outside
	const handleOverlayClick = useCallback(
		(e: React.MouseEvent<HTMLDivElement>) => {
			if (e.target === e.currentTarget) {
				closeSearch()
			}
		},
		[closeSearch]
	)

	// Don't render anything if not open
	if (!isOpen) return null

	// Show loading spinner when searching or loading data
	const showSpinner = isSearching || isLoadingData || isLoadingSuggestions
	const showStockSpinner = isStockSearching

	return (
		<div className={styles.overlay} onClick={handleOverlayClick}>
			<div className={styles.container} onClick={e => e.stopPropagation()}>
				{stockContainerVisible && selectedStock ? (
					<>
						{/* Stock Search Input */}
						<div className={styles.searchInputContainer}>
							<IconButton onClick={returnToMainSearch} variant="tertiary" size="micro" style={{ padding: 0 }}>
								<BlockIcon icon={SmallLeftArrow} size="medium" color="neutral.300" />
							</IconButton>
							<input
								ref={stockInputRef}
								className={styles.searchInput}
								type="text"
								placeholder={`Search ${selectedStock.label} options...`}
								value={stockSearchValue}
								onChange={handleStockSearchChange}
								onKeyDown={handleStockContainerKeyDown}
							/>
							{showStockSpinner && <div className={styles.searchSpinner} aria-hidden="true" />}
							<IconButton onClick={closeSearch} variant="tertiary" size="medium" className={styles.closeButton}>
								<BlockIcon icon={CloseIcon} size="medium" color="neutral.300" />
							</IconButton>
						</div>

						{/* Stock Options Container */}
						<StockContainer
							stock={selectedStock}
							selectedOptionIndex={selectedOptionIndex}
							onSelect={handleStockOptionSelect}
							onKeyDown={handleStockContainerKeyDown}
							searchQuery={stockSearchValue}
							companyInfo={companyInfoData}
							isCompanyInfoLoading={isCompanyInfoLoading}
							stockOptions={stockOptions}
						/>
					</>
				) : (
					<>
						{/* Main Search Input */}
						<div className={styles.searchInputContainer}>
							<IconButton onClick={() => {}} variant="tertiary" size="micro" style={{ padding: 0 }}>
								<BlockIcon icon={SearchIcon} size="medium" color="neutral.300" />
							</IconButton>
							<input
								ref={inputRef}
								className={styles.searchInput}
								type="text"
								placeholder="Navigate, search for stocks..."
								value={inputValue}
								onChange={handleInputChange}
								onKeyDown={handleKeyDown}
							/>
							{showSpinner && <div className={styles.searchSpinner} aria-hidden="true" />}
							<IconButton onClick={closeSearch} variant="tertiary" size="medium" className={styles.closeButton}>
								<BlockIcon icon={CloseIcon} size="medium" color="neutral.300" />
							</IconButton>
						</div>

						{/* Welcome Header - Only show when no input and no suggestions */}
						{!inputValue.trim() && (
							<div className={styles.headerContainer}>
								<Box className={styles.dnaIcon}>
									<BlockIcon icon={PiDnaFill} color="neutral.300" />
								</Box>
								<Text variant="body1Bold" className={styles.welcomeTitle}>
									Helix Engaged. Begin Traversal.
								</Text>
								<Box display="flex" flexDirection="column" gap={2}>
									<Text variant="body1" color="neutral.300" className={styles.welcomeSubtitle}>
										Precision routing for accelerated thought.
									</Text>
									<Text variant="metadata" color="neutral.300" className={styles.shortcutHint}>
										Use <span className={styles.keyboardKey}>{modifierKey}</span> +{' '}
										<span className={styles.keyboardKey}>M</span> to open Helix anytime
									</Text>
								</Box>
							</div>
						)}

						{/* Regular Results Container */}
						<div ref={resultsRef} className={styles.resultsContainer} role="listbox" aria-label="Search results">
							{rowVirtualizer.getVirtualItems().length === 0 && (
								<Box className={styles.loadingContainer}>
									<Text textAlign="center" color="neutral.300">
										Loading...
									</Text>
								</Box>
							)}

							{displayResults.length > 0 && (
								<div
									style={{
										height: `${rowVirtualizer.getTotalSize()}px`,
										width: '100%',
										position: 'relative'
									}}>
									{rowVirtualizer.getVirtualItems().map(virtualRow => (
										<ResultItem
											key={displayResults[virtualRow.index].id}
											item={displayResults[virtualRow.index]}
											index={virtualRow.index}
											isSelected={selectedIndex === virtualRow.index}
											isFocused={selectedIndex === virtualRow.index}
											onClick={() => handleSelect(displayResults[virtualRow.index])}
											onKeyDown={handleKeyDown}
											innerRef={rowVirtualizer.measureElement}
											virtualStyles={{
												position: 'absolute',
												top: 0,
												left: 0,
												width: '100%',
												transform: `translateY(${virtualRow.start}px)`
											}}
											searchTerm={inputValue}
										/>
									))}
								</div>
							)}
						</div>
					</>
				)}

				{/* Footer with shortcuts */}
				<Box display="flex" gap={5} justifyContent="space-between" className={styles.footer}>
					<Box display="flex" gap={2}>
						<span className={styles.keyboardShortcut}>
							<span className={styles.keyboardKey}>↑</span>
							<span className={styles.keyboardKey}>↓</span>
							to navigate
						</span>
						{stockContainerVisible && (
							<span className={styles.keyboardShortcut} style={{ marginLeft: '8px' }}>
								<span className={styles.keyboardKey}>Backspace</span>
								to go back
							</span>
						)}
					</Box>
					<Box display="flex" gap={2}>
						<span className={styles.keyboardShortcut}>
							<span className={styles.keyboardKey}>Enter</span>
							to select
						</span>
						<span className={styles.keyboardShortcut} style={{ marginLeft: '8px' }}>
							<span className={styles.keyboardKey}>Esc</span>
							to close
						</span>
					</Box>
				</Box>
			</div>
		</div>
	)
}

export default Helix
